/**
* Hand-written translation & certification Q&A sets for the 6 non-Thai locales.
*
* Powers /{locale}/translation/questions. Each locale ships 10 uniquely
* authored answers (not machine translations of the Thai library) so every
* localized hub earns its own E-E-A-T signal and can be quoted verbatim by
* AI answer engines.
*
* Accuracy policy (reference date: August 2026):
* - Thailand has no state "sworn translator" register. A translation used
* abroad is authenticated through the Department of Consular Affairs,
* Ministry of Foreign Affairs (Chaeng Watthana) — the MFA certifies the
* signature/seal chain, not the linguistic quality of the translation.
* - MFA legalisation fees: 200 THB per document (normal, collection usually
* on the 2nd business day) and 400 THB per document (express, same day).
* Fees are set by the MFA and may change; verify before filing.
* - Thailand acceded to the 1961 Hague Apostille Convention in June 2026 and
* the Convention enters into force for Thailand on 28 February 2027. Until
* that date the MFA -> destination embassy legalisation chain still applies.
* - NAATI certification applies to Australia (and is commonly accepted in
* New Zealand). It is not a substitute for MFA legalisation and is not
* required by Thai authorities.
* - Notarisation in Thailand is performed by a Notarial Services Attorney
* licensed by the Lawyers Council of Thailand under the 2003 (B.E. 2546)
* regulation; it certifies signatures and copies, not translation accuracy.
* - Every timeframe and fee below is an estimate. The receiving authority
* always sets the final requirement and no outcome is guaranteed.
*/

export type MltLocale = "en" | "zh" | "ja" | "ko" | "vi" | "id";

export interface MltLocaleQaCopy {
  hreflang: string;
  title: string;
  description: string;
  h1: string;
  lead: string;
  answerLabel: string;
  pillarLabel: string;
  thaiSetLabel: string;
  notaryLabel: string;
  ctaTitle: string;
  ctaText: string;
  disclaimer: string;
  sourcesLabel: string;
  items: Array<{ q: string; a: string }>;
}

export const MLT_QA_LOCALES: MltLocale[] = ["en", "zh", "ja", "ko", "vi", "id"];

export const MLT_QA_LOCALE: Record<MltLocale, MltLocaleQaCopy> = {
  en: {
    hreflang: "en",
    title: "Certified Translation in Thailand — FAQ for Every Language",
    description:
      "How certified translation and legalisation really work in Thailand: who may certify, when MFA legalisation is required, embassy steps, apostille timing, NAATI scope and the usual rejection causes.",
    h1: "Certified Translation & Document Legalisation in Thailand — FAQ",
    lead:
      "Thailand keeps no state register of sworn translators. A translation leaving Thailand is authenticated through the Department of Consular Affairs of the Ministry of Foreign Affairs, which certifies the signature and seal chain rather than the wording itself. The answers below reflect practice as of August 2026; fees and lead times are estimates and the receiving authority sets the final requirement.",
    answerLabel: "Answer",
    pillarLabel: "Translation service overview",
    thaiSetLabel: "Full Thai Q&A library",
    notaryLabel: "Notary Public in Thailand",
    ctaTitle: "Not sure which certification your document needs?",
    ctaText:
      "Send a scan plus the name of the authority that will receive the file. We confirm the exact chain, estimated cost and lead time before any work begins.",
    disclaimer:
      "General information only, not legal advice, and no outcome is guaranteed. Requirements change — confirm with the authority that will receive your documents.",
    sourcesLabel:
      "Primary sources: Department of Consular Affairs, Ministry of Foreign Affairs of Thailand; HCCH status table for the 1961 Apostille Convention; NAATI (Australia); Lawyers Council of Thailand notarial services regulation.",
    items: [
      {
        q: "Is there such a thing as a sworn translator in Thailand?",
        a: "No. Unlike Germany, France, Spain or Indonesia, Thailand has no court-appointed sworn translator register. A Thai translation gains official weight in two other ways: a certificate of accuracy issued by the translation company or translator, and — for documents leaving Thailand — legalisation of the translation by the Department of Consular Affairs of the Ministry of Foreign Affairs. When a foreign authority asks for a 'sworn translation', it normally means the MFA-legalised route, or a sworn translator appointed in that country.",
      },
      {
        q: "When does my translation actually need MFA legalisation?",
        a: "Whenever a foreign government body will act on the document: visa and immigration files, marriage registration abroad, university admission, professional licensing, company registration, court filings and property transactions. Private uses — an employer's HR file, a school application that only asks for a certified copy, an internal audit — usually accept a company certificate of accuracy. Ask the receiving office in writing which level it requires before paying for the chain.",
      },
      {
        q: "What does the Department of Consular Affairs charge and how long does it take?",
        a: "The MFA fee is 200 THB per document for the normal service, with collection normally on the second business day, and 400 THB per document for the express same-day service. Those are official government fees, separate from translation and courier costs, and the MFA may change them. Volume, document condition and queue length at Chaeng Watthana all affect real turnaround, so plan a working-day buffer rather than counting on the minimum.",
      },
      {
        q: "Can Thailand issue an apostille yet?",
        a: "Not yet. Thailand deposited its instrument of accession to the 1961 Hague Apostille Convention in June 2026 and the Convention enters into force for Thailand on 28 February 2027. Until that date every Thai document destined for a Convention country still follows the traditional chain: translation, MFA legalisation, then legalisation by the destination country's embassy or consulate in Bangkok. Confirm the current status with the Department of Consular Affairs before you file.",
      },
      {
        q: "Does the embassy step disappear once apostille starts?",
        a: "For Convention member states the apostille is designed to replace embassy legalisation for public documents. Non-member destinations — several Middle Eastern and a few Asian jurisdictions — will still require embassy legalisation. Some receiving institutions also keep their own internal rules, for example insisting on a translation produced in their own country. Treat the apostille as replacing one step in the chain, not as replacing translation or the destination authority's discretion.",
      },
      {
        q: "Is a NAATI translation useful for documents used inside Thailand?",
        a: "Generally no. NAATI is the Australian certification body, and its stamp is what the Department of Home Affairs, Australian universities and state agencies expect. Thai district offices, immigration and courts do not recognise NAATI as a domestic certification; they want a Thai translation whose accuracy is certified and, where the document is foreign, legalised through the MFA. Use NAATI for the Australia-bound direction and the consular route for the Thailand-bound direction.",
      },
      {
        q: "What is the correct order for a foreign document I need to use in Thailand?",
        a: "Authenticate first, translate second. The document is certified in the issuing country (notary, then the competent authority or that country's foreign ministry), then legalised by the Royal Thai Embassy or Consulate there when the apostille route is unavailable. Only then is it translated into Thai and the translation legalised by the Department of Consular Affairs. Translating before authentication is the single most common reason a file has to be redone from scratch.",
      },
      {
        q: "How fresh does a civil registration document have to be?",
        a: "Most destination authorities want a certified extract issued within three to six months, and some embassies apply the tighter three-month rule to marital status and police clearance certificates. Because the whole chain can consume two to three weeks, request the fresh extract from the district office first and start the clock from that date. An extract that expires while it waits in an embassy queue cannot be rescued — the chain restarts.",
      },
      {
        q: "Why do embassies reject otherwise correct translations?",
        a: "The recurring causes are name spellings that differ from the passport, translating from an uncertified photocopy instead of a fresh certified extract, omitting the back page, stamps or handwritten annotations, document or registration numbers that do not match the original, staples removed so a seal no longer bridges the pages, and an expired source extract. Each of these is a formal defect the reviewing officer can raise without assessing the translation itself.",
      },
      {
        q: "Do you translate directly between two foreign languages?",
        a: "For most pairs we route through English or Thai, and that is usually what the receiving authority prefers because it can verify the pivot text. Where a destination insists on a direct pair — for instance a Japanese document for a Korean ministry — we confirm in advance whether that authority accepts a pivot version, since redoing the chain later costs far more than checking first. Tell us the destination office by name and we scope the pair accordingly.",
      },
    ],
  },

  zh: {
    hreflang: "zh",
    title: "泰国翻译认证常见问题 — 领事认证与使馆流程解析",
    description:
      "在泰国办理翻译认证的完整答案：谁有资格认证、何时需要外交部领事司认证、使馆加签步骤、海牙认证生效时间、NAATI 适用范围以及最常见的退件原因。",
    h1: "泰国翻译与文件认证常见问题",
    lead:
      "泰国没有国家宣誓翻译登记制度。文件出境时，由外交部领事司（Chaeng Watthana）对签名与印章链条进行认证，而不是对译文措辞作出保证。以下内容依据 2026 年 8 月的实务撰写，费用与工作日均为估算，最终要求由接收机关决定。",
    answerLabel: "解答",
    pillarLabel: "翻译服务总览",
    thaiSetLabel: "泰文完整问答库",
    notaryLabel: "泰国 Notary Public 律师认证",
    ctaTitle: "不确定您的文件需要哪一级认证？",
    ctaText: "请提供文件扫描件与接收机关名称，我们会在开始作业前确认完整链条、预估费用与工作日。",
    disclaimer: "以上仅为一般性信息，不构成法律意见，也不保证任何审批结果。规定会变动，请以接收文件的机关要求为准。",
    sourcesLabel:
      "主要依据：泰国外交部领事司；海牙国际私法会议（HCCH）1961 年取消认证公约状态表；澳大利亚 NAATI；泰国律师公会公证律师规定。",
    items: [
      {
        q: "泰国有「宣誓翻译」制度吗？",
        a: "没有。与德国、法国、西班牙、印尼不同，泰国没有法院指定的宣誓翻译登记名册。泰国译文取得官方效力有两条路径：翻译公司或译者出具的准确性声明，以及文件出境时由外交部领事司对译文进行认证。当国外机关要求「宣誓翻译」时，通常指的就是领事司认证这条路线，或者由该国本地宣誓翻译出具的译文。",
      },
      {
        q: "什么情况下译文必须经外交部领事司认证？",
        a: "只要有外国政府机关据此作出决定，就需要：签证与移民材料、境外结婚登记、大学入学、专业执照、公司注册、法院文件与不动产交易。若仅供私人用途，例如雇主人事档案、只要求核证副本的学校申请、内部审计，一般接受公司出具的准确性声明即可。付费办理链条之前，请先书面向接收单位确认所需层级。",
      },
      {
        q: "领事司收费与办理时间是多少？",
        a: "外交部官方规费为普通件每份 200 泰铢，通常第二个工作日领取；加急件每份 400 泰铢，可当日领取。这是政府规费，与翻译费、快递费分开计算，且外交部可能调整。实际时效受件数、文件状况与 Chaeng Watthana 现场排队影响，建议预留一个工作日的缓冲，不要以最短时间为准。",
      },
      {
        q: "泰国现在可以出具海牙认证（Apostille）吗？",
        a: "尚不可以。泰国已于 2026 年 6 月递交 1961 年海牙取消认证公约加入书，公约对泰国自 2027 年 2 月 28 日起生效。在此之前，前往公约成员国的泰国文件仍须走传统链条：翻译、外交部领事司认证，再由目的地国驻曼谷使领馆加签。递件前请向领事司核实最新状态。",
      },
      {
        q: "海牙认证生效后还需要使馆加签吗？",
        a: "对公约成员国而言，海牙认证的设计目的就是取代使馆加签这一步。非成员国目的地——包括部分中东与少数亚洲司法辖区——仍须使馆认证。此外，部分接收机构会另有内部规定，例如坚持要求在其本国境内完成的译文。请把海牙认证理解为取代链条中的一环，而不是取代翻译本身或目的地机关的裁量权。",
      },
      {
        q: "NAATI 译文能在泰国境内使用吗？",
        a: "通常不能。NAATI 是澳大利亚的翻译认证机构，澳洲内政部、大学与州政府机关认可其印章。泰国区役所、移民局与法院并不将 NAATI 视为境内认证，它们要求泰文译文附准确性证明；文件若为外国出具，还须经领事司认证。简言之：赴澳方向用 NAATI，入泰方向走领事认证。",
      },
      {
        q: "外国文件要在泰国使用，正确顺序是什么？",
        a: "先认证、后翻译。文件先在出具国完成认证（公证人，再到主管机关或该国外交部），在无法走海牙认证时，再由泰国驻该国使领馆认证。完成之后才译成泰文，并由外交部领事司认证译文。先翻译后认证，是文件必须整套重做的最常见原因。",
      },
      {
        q: "户籍类文件的有效期限是多久？",
        a: "多数目的地机关要求三至六个月内签发的核证副本，部分使馆对单身证明与无犯罪记录证明采用更严格的三个月标准。由于整条链条可能耗时两到三周，请先到区役所申领新的核证副本，再从该日期起算。若副本在使馆排队期间过期，无法补救，只能重跑整条链条。",
      },
      {
        q: "译文正确为何仍被退件？",
        a: "常见原因包括：姓名拼写与护照不一致、以未核证的复印件而非新签发的核证副本作为翻译底本、漏译背面、印章或手写批注、文件编号与原件不符、拆除订书针导致骑缝章断裂、以及底本已过有效期。这些都属于形式瑕疵，审核人员无需评价译文质量即可退件。",
      },
      {
        q: "可以直接办理两种外语之间的互译吗？",
        a: "多数语对我们会以英文或泰文作为中转，这通常也是接收机关偏好的方式，因为它可以核对中转文本。若目的地坚持要求直译语对——例如日文文件送交韩国部委——我们会事先确认该机关是否接受中转版本，因为事后重跑链条的成本远高于事前确认。请提供接收机关的具体名称，我们据此规划语对。",
      },
    ],
  },

  ja: {
    hreflang: "ja",
    title: "タイの翻訳証明FAQ — 領事認証・大使館認証の実務解説",
    description:
      "タイでの翻訳証明と認証の全体像：誰が証明できるか、外務省領事局の認証が必要な場面、大使館認証の手順、アポスティーユの発効時期、NAATIの適用範囲、そして差戻しの典型原因を解説します。",
    h1: "タイの翻訳証明・書類認証に関するよくあるご質問",
    lead:
      "タイには国が管理する宣誓翻訳者の登録制度がありません。国外で使う書類は、外務省領事局（チェンワッタナ）が署名と印章の連鎖を認証します。訳文の文言そのものを保証する制度ではありません。以下は2026年8月時点の実務であり、費用と日数は目安です。最終的な要件は提出先機関が決定します。",
    answerLabel: "回答",
    pillarLabel: "翻訳サービスの概要",
    thaiSetLabel: "タイ語の全問答ライブラリ",
    notaryLabel: "タイのノータリー（公証弁護士）",
    ctaTitle: "どの段階の認証が必要か判断がつきませんか。",
    ctaText: "書類のスキャンと提出先機関の正式名称をお送りください。着手前に必要な工程、概算費用、営業日数をご案内します。",
    disclaimer:
      "本ページは一般的な情報提供であり法的助言ではありません。結果を保証するものでもありません。要件は変更されるため、提出先機関に必ずご確認ください。",
    sourcesLabel:
      "主要典拠：タイ外務省領事局、HCCH（1961年アポスティーユ条約ステータス表）、NAATI（オーストラリア）、タイ弁護士会のノータリアルサービス規則。",
    items: [
      {
        q: "タイに「宣誓翻訳者」制度はありますか。",
        a: "ありません。ドイツ、フランス、スペイン、インドネシアと異なり、タイには裁判所が任命する宣誓翻訳者の登録名簿が存在しません。タイの訳文が公的な重みを持つ経路は二つで、翻訳会社または翻訳者が発行する正確性証明書と、国外で使う書類について外務省領事局が行う訳文の認証です。外国機関が「宣誓翻訳」を求める場合、実務上は領事局ルート、または当該国で任命された宣誓翻訳者を指します。",
      },
      {
        q: "外務省領事局の認証が必要になるのはどんな場合ですか。",
        a: "外国の政府機関がその書類に基づいて判断を行う場合です。査証・移民申請、国外での婚姻登録、大学出願、専門職資格、会社登記、裁判所提出、不動産取引が該当します。雇用主の人事ファイル、認証謄本のみを求める学校出願、社内監査といった私的用途では、会社発行の正確性証明書で足りることが一般的です。費用をかける前に、提出先へ書面で必要な段階を確認してください。",
      },
      {
        q: "領事局の手数料と所要日数を教えてください。",
        a: "外務省の公定手数料は、通常扱いが1通200バーツで受取は原則として翌々営業日、特急扱いが1通400バーツで即日受取です。これは政府手数料であり、翻訳料や配送料とは別枠で、外務省の判断で改定される可能性があります。実際の所要日数は通数、書類の状態、チェンワッタナ窓口の混雑に左右されるため、最短日数を前提とせず1営業日の余裕を見てください。",
      },
      {
        q: "タイはアポスティーユを発行できますか。",
        a: "現時点ではできません。タイは2026年6月に1961年ハーグ・アポスティーユ条約の加入書を寄託し、同条約はタイについて2027年2月28日に発効します。それまでは、条約加盟国向けのタイ書類も従来の連鎖、すなわち翻訳、外務省領事局の認証、バンコク所在の提出先国大使館・領事館による認証をたどります。提出前に領事局で最新の状況をご確認ください。",
      },
      {
        q: "アポスティーユ開始後、大使館認証は不要になりますか。",
        a: "条約加盟国向けの公文書については、アポスティーユが大使館認証を代替する設計です。非加盟国、たとえば一部の中東諸国や少数のアジアの法域では引き続き大使館認証が必要です。さらに、提出先が自国内で作成された訳文を求めるなど独自の内部規則を維持している場合もあります。アポスティーユは連鎖の一工程を置き換えるものであり、翻訳自体や提出先の裁量を置き換えるものではありません。",
      },
      {
        q: "NAATI翻訳はタイ国内で使えますか。",
        a: "通常は使えません。NAATIはオーストラリアの認証機関で、内務省、大学、州政府機関が想定しているのはその証印です。タイの区役所、入国管理局、裁判所はNAATIを国内の認証とは扱わず、正確性が証明されたタイ語訳を、外国発行書類であれば領事局の認証を経たうえで求めます。豪州方向はNAATI、タイ方向は領事ルートと整理してください。",
      },
      {
        q: "外国書類をタイで使う場合の正しい順序は何ですか。",
        a: "先に認証、後に翻訳です。まず発行国で認証を受け（公証人、次に所轄官庁または同国外務省）、アポスティーユが使えない場合は当該国の在外タイ大使館・領事館の認証を受けます。その後にタイ語へ翻訳し、訳文を外務省領事局で認証します。認証前に翻訳してしまうことが、一式やり直しになる最も多い原因です。",
      },
      {
        q: "戸籍関係書類の有効期間はどの程度ですか。",
        a: "多くの提出先は発行後3か月から6か月以内の認証謄本を求め、独身証明書や無犯罪証明書には3か月の厳しい基準を適用する大使館もあります。連鎖全体で2〜3週間かかり得るため、まず区役所で新しい謄本を取得し、その日付から逆算してください。大使館の待機中に有効期間が切れた謄本は救済できず、連鎖をやり直すことになります。",
      },
      {
        q: "訳文が正確でも差し戻されるのはなぜですか。",
        a: "典型的な原因は、氏名の綴りが旅券と一致しない、認証されていないコピーを原稿にした、裏面・印影・手書き注記を訳出していない、書類番号が原本と一致しない、ホチキスを外して割印が途切れた、原本の有効期間が切れている、といった形式面の瑕疵です。いずれも審査官が訳文の質を評価するまでもなく指摘できる事項です。",
      },
      {
        q: "外国語同士の直接翻訳は可能ですか。",
        a: "多くの言語ペアでは英語またはタイ語を経由します。提出先も中間言語版を照合できるため、この方式を好むことが一般的です。提出先が直接ペアを要求する場合、たとえば日本語書類を韓国の省庁へ提出するようなケースでは、経由版が受理されるかを事前に確認します。後から連鎖をやり直す費用は事前確認より格段に大きいためです。提出先の正式名称をお知らせください。",
      },
    ],
  },

  ko: {
    hreflang: "ko",
    title: "태국 번역공증 FAQ — 영사확인·대사관 인증 절차 안내",
    description:
      "태국에서 번역과 인증이 실제로 어떻게 진행되는지 정리했습니다. 인증 주체, 외교부 영사국 확인이 필요한 경우, 대사관 인증 절차, 아포스티유 발효 시점, NAATI 적용 범위, 반려 사유까지 담았습니다.",
    h1: "태국 번역·문서 인증 자주 묻는 질문",
    lead:
      "태국에는 국가가 관리하는 선서번역사 등록제도가 없습니다. 국외에서 사용할 문서는 외교부 영사국(챙왓타나)이 서명과 직인의 연결고리를 확인하며, 번역 문구 자체를 보증하지는 않습니다. 아래 내용은 2026년 8월 실무 기준이며 비용과 소요일은 추정치입니다. 최종 요건은 접수 기관이 정합니다.",
    answerLabel: "답변",
    pillarLabel: "번역 서비스 개요",
    thaiSetLabel: "태국어 전체 Q&A",
    notaryLabel: "태국 노터리(공증변호사)",
    ctaTitle: "어느 단계의 인증이 필요한지 모르시겠습니까?",
    ctaText: "문서 스캔본과 접수 기관의 정확한 명칭을 보내주시면, 착수 전에 필요한 절차와 예상 비용, 소요 영업일을 확인해 드립니다.",
    disclaimer: "일반 정보 제공일 뿐 법률 자문이 아니며 어떠한 결과도 보장하지 않습니다. 요건은 변경되므로 접수 기관에 반드시 확인하십시오.",
    sourcesLabel:
      "주요 출처: 태국 외교부 영사국, HCCH 1961년 아포스티유 협약 현황표, 호주 NAATI, 태국 변호사협회 공증업무 규정.",
    items: [
      {
        q: "태국에도 선서번역사 제도가 있습니까?",
        a: "없습니다. 독일, 프랑스, 스페인, 인도네시아와 달리 태국에는 법원이 임명하는 선서번역사 등록부가 존재하지 않습니다. 태국 번역문이 공적 효력을 갖는 경로는 두 가지로, 번역회사나 번역사가 발급하는 정확성 확인서, 그리고 국외 사용 문서에 대해 외교부 영사국이 수행하는 번역문 확인입니다. 외국 기관이 선서번역을 요구할 때는 통상 영사국 경로 또는 해당 국가에서 임명된 선서번역사를 의미합니다.",
      },
      {
        q: "번역문에 영사국 확인이 반드시 필요한 경우는 언제입니까?",
        a: "외국 정부기관이 그 문서를 근거로 판단을 내리는 경우입니다. 비자와 이민 서류, 국외 혼인신고, 대학 입학, 전문자격 등록, 법인 설립, 법원 제출, 부동산 거래가 해당합니다. 고용주 인사 파일, 인증등본만 요구하는 학교 지원, 내부 감사처럼 사적 용도라면 회사 발급 정확성 확인서로 충분한 경우가 많습니다. 비용을 지출하기 전에 접수처에 필요한 단계를 서면으로 확인하십시오.",
      },
      {
        q: "영사국 수수료와 소요 기간은 어떻게 됩니까?",
        a: "외교부 공식 수수료는 일반이 문서당 200바트이며 통상 두 번째 영업일에 수령하고, 특급은 문서당 400바트로 당일 수령이 가능합니다. 이는 정부 수수료로 번역료와 배송비와는 별도이며 외교부가 변경할 수 있습니다. 실제 소요 기간은 통수, 문서 상태, 챙왓타나 창구 대기 상황에 따라 달라지므로 최소 일정을 전제로 하지 말고 영업일 하루의 여유를 두십시오.",
      },
      {
        q: "태국에서 아포스티유를 받을 수 있습니까?",
        a: "아직 불가능합니다. 태국은 2026년 6월 1961년 헤이그 아포스티유 협약 가입서를 기탁했고, 협약은 2027년 2월 28일 태국에 대해 발효합니다. 그때까지는 협약 가입국으로 보내는 태국 문서도 기존 절차, 즉 번역, 외교부 영사국 확인, 방콕 주재 목적국 대사관 또는 영사관 인증을 거칩니다. 제출 전에 영사국에서 최신 상태를 확인하십시오.",
      },
      {
        q: "아포스티유가 시작되면 대사관 인증은 없어집니까?",
        a: "협약 가입국으로 보내는 공문서에 대해서는 아포스티유가 대사관 인증을 대체하도록 설계되어 있습니다. 비가입국, 예를 들어 일부 중동 국가와 소수의 아시아 관할권에서는 여전히 대사관 인증이 필요합니다. 또한 접수 기관이 자국 내에서 작성된 번역문을 요구하는 등 자체 규정을 유지하기도 합니다. 아포스티유는 절차의 한 단계를 대체할 뿐 번역 자체나 접수 기관의 재량을 대체하지 않습니다.",
      },
      {
        q: "NAATI 번역을 태국 국내에서 사용할 수 있습니까?",
        a: "일반적으로 사용할 수 없습니다. NAATI는 호주의 인증 기관이며 내무부, 대학, 주정부 기관이 요구하는 것이 그 직인입니다. 태국 구청, 이민국, 법원은 NAATI를 국내 인증으로 인정하지 않고, 정확성이 확인된 태국어 번역문을 요구하며 외국 발행 문서라면 영사국 확인까지 요구합니다. 호주 방향은 NAATI, 태국 방향은 영사 경로로 구분하십시오.",
      },
      {
        q: "외국 문서를 태국에서 쓰려면 순서가 어떻게 됩니까?",
        a: "인증이 먼저, 번역이 나중입니다. 먼저 발행국에서 인증을 받고(공증인, 이어서 관할 기관 또는 그 나라 외교부), 아포스티유 경로를 쓸 수 없으면 해당 국가의 주재 태국 대사관 또는 영사관 인증을 받습니다. 그다음 태국어로 번역하고 번역문을 외교부 영사국에서 확인받습니다. 인증 전에 번역하는 것이 서류 일체를 다시 만들게 되는 가장 흔한 원인입니다.",
      },
      {
        q: "가족관계 서류의 유효기간은 어느 정도입니까?",
        a: "대다수 접수 기관은 발급 후 3개월에서 6개월 이내의 인증등본을 요구하며, 혼인관계증명이나 범죄경력증명에는 3개월 기준을 적용하는 대사관도 있습니다. 전체 절차에 2~3주가 걸릴 수 있으므로 구청에서 새 등본을 먼저 발급받고 그 날짜를 기준으로 일정을 잡으십시오. 대사관 대기 중 유효기간이 지난 등본은 구제되지 않고 절차를 다시 밟아야 합니다.",
      },
      {
        q: "번역이 정확한데도 반려되는 이유는 무엇입니까?",
        a: "이름 철자가 여권과 다른 경우, 인증되지 않은 사본을 원고로 삼은 경우, 뒷면과 직인, 수기 기재를 번역하지 않은 경우, 문서 번호가 원본과 불일치하는 경우, 스테이플을 제거해 간인이 끊긴 경우, 원본 등본의 유효기간이 지난 경우가 반복되는 사유입니다. 모두 심사관이 번역 품질을 평가하지 않고도 지적할 수 있는 형식 하자입니다.",
      },
      {
        q: "외국어 간 직접 번역도 가능합니까?",
        a: "대부분의 언어쌍은 영어 또는 태국어를 경유합니다. 접수 기관이 중간 언어본을 대조할 수 있어 이 방식을 선호하는 경우가 많습니다. 목적지가 직접 언어쌍을 요구하는 경우, 예컨대 일본어 문서를 한국 부처에 제출할 때는 경유본 수용 여부를 사전에 확인합니다. 나중에 절차를 다시 밟는 비용이 사전 확인보다 훨씬 크기 때문입니다. 접수 기관의 정확한 명칭을 알려주십시오.",
      },
    ],
  },

  vi: {
    hreflang: "vi",
    title: "Hỏi đáp dịch thuật công chứng tại Thái Lan và hợp pháp hóa",
    description:
      "Giải đáp đầy đủ về dịch thuật và hợp pháp hóa tại Thái Lan: ai được chứng nhận, khi nào cần Cục Lãnh sự Bộ Ngoại giao, bước đại sứ quán, thời điểm Apostille có hiệu lực và các lỗi bị trả hồ sơ.",
    h1: "Dịch thuật và hợp pháp hóa giấy tờ tại Thái Lan — Hỏi đáp",
    lead:
      "Thái Lan không có danh bạ dịch thuật viên tuyên thệ do nhà nước quản lý. Giấy tờ mang ra nước ngoài được Cục Lãnh sự thuộc Bộ Ngoại giao (Chaeng Watthana) chứng nhận chuỗi chữ ký và con dấu, chứ không bảo đảm câu chữ của bản dịch. Nội dung dưới đây theo thực tiễn tháng 8 năm 2026; phí và số ngày làm việc là ước tính, cơ quan tiếp nhận quyết định yêu cầu cuối cùng.",
    answerLabel: "Trả lời",
    pillarLabel: "Tổng quan dịch vụ dịch thuật",
    thaiSetLabel: "Thư viện hỏi đáp tiếng Thái",
    notaryLabel: "Luật sư Notary Public tại Thái Lan",
    ctaTitle: "Chưa rõ hồ sơ của bạn cần cấp độ chứng nhận nào?",
    ctaText: "Gửi bản scan kèm tên cơ quan tiếp nhận. Chúng tôi xác nhận đúng chuỗi thủ tục, chi phí ước tính và số ngày làm việc trước khi bắt đầu.",
    disclaimer:
      "Đây là thông tin chung, không phải tư vấn pháp lý và không bảo đảm kết quả. Quy định có thể thay đổi — hãy xác nhận với cơ quan tiếp nhận hồ sơ.",
    sourcesLabel:
      "Nguồn chính: Cục Lãnh sự, Bộ Ngoại giao Thái Lan; bảng trạng thái Công ước Apostille 1961 của HCCH; NAATI (Úc); quy định về dịch vụ công chứng của Liên đoàn Luật sư Thái Lan.",
    items: [
      {
        q: "Thái Lan có chế độ dịch thuật viên tuyên thệ không?",
        a: "Không. Khác với Đức, Pháp, Tây Ban Nha hay Indonesia, Thái Lan không có danh bạ dịch thuật viên tuyên thệ do tòa án bổ nhiệm. Bản dịch tại Thái Lan có giá trị chính thức theo hai cách: giấy xác nhận độ chính xác do công ty dịch thuật hoặc biên dịch viên cấp, và với giấy tờ dùng ở nước ngoài là việc Cục Lãnh sự Bộ Ngoại giao chứng nhận bản dịch. Khi cơ quan nước ngoài yêu cầu bản dịch tuyên thệ, thường họ muốn nói tới tuyến Cục Lãnh sự hoặc dịch giả tuyên thệ tại chính nước đó.",
      },
      {
        q: "Khi nào bản dịch bắt buộc phải qua Cục Lãnh sự?",
        a: "Khi một cơ quan nhà nước nước ngoài sẽ ra quyết định dựa trên giấy tờ đó: hồ sơ thị thực và di trú, đăng ký kết hôn ở nước ngoài, nhập học đại học, cấp phép hành nghề, đăng ký doanh nghiệp, nộp tòa và giao dịch bất động sản. Với mục đích tư nhân như hồ sơ nhân sự của công ty, đơn nhập học chỉ cần bản sao chứng thực hay kiểm toán nội bộ, giấy xác nhận độ chính xác của công ty thường là đủ. Hãy hỏi bằng văn bản trước khi trả phí cho cả chuỗi.",
      },
      {
        q: "Phí và thời gian tại Cục Lãnh sự là bao nhiêu?",
        a: "Phí chính thức của Bộ Ngoại giao là 200 baht mỗi văn bản cho dịch vụ thường, thường nhận vào ngày làm việc thứ hai, và 400 baht mỗi văn bản cho dịch vụ nhanh lấy trong ngày. Đây là phí nhà nước, tách biệt với phí dịch và phí chuyển phát, và Bộ Ngoại giao có thể điều chỉnh. Thời gian thực tế phụ thuộc số lượng, tình trạng giấy tờ và lượng hồ sơ chờ tại Chaeng Watthana, nên hãy dự phòng thêm một ngày làm việc.",
      },
      {
        q: "Thái Lan đã cấp được Apostille chưa?",
        a: "Chưa. Thái Lan nộp văn kiện gia nhập Công ước Apostille La Hay 1961 vào tháng 6 năm 2026 và Công ước có hiệu lực với Thái Lan từ ngày 28 tháng 2 năm 2027. Trước thời điểm đó, mọi giấy tờ Thái Lan gửi tới nước thành viên vẫn theo chuỗi truyền thống: dịch, chứng nhận tại Cục Lãnh sự, rồi hợp pháp hóa tại đại sứ quán hoặc lãnh sự quán của nước đến tại Bangkok. Hãy kiểm tra trạng thái hiện hành với Cục Lãnh sự trước khi nộp.",
      },
      {
        q: "Khi Apostille bắt đầu thì còn cần bước đại sứ quán không?",
        a: "Với các nước thành viên Công ước, Apostille được thiết kế để thay thế bước hợp pháp hóa tại đại sứ quán đối với giấy tờ công. Các nước không phải thành viên, gồm một số quốc gia Trung Đông và vài khu vực tài phán châu Á, vẫn yêu cầu hợp pháp hóa lãnh sự. Ngoài ra một số cơ quan tiếp nhận vẫn giữ quy định riêng, chẳng hạn buộc bản dịch phải thực hiện trong nước họ. Apostille thay một mắt xích, không thay việc dịch hay quyền quyết định của cơ quan tiếp nhận.",
      },
      {
        q: "Bản dịch NAATI có dùng được trong nội địa Thái Lan không?",
        a: "Thường là không. NAATI là cơ quan chứng nhận của Úc và con dấu NAATI là thứ Bộ Nội vụ Úc, các trường đại học và cơ quan bang mong đợi. Cơ quan quận, Cục Xuất nhập cảnh và tòa án Thái Lan không coi NAATI là chứng nhận trong nước; họ cần bản dịch tiếng Thái có xác nhận độ chính xác và, nếu giấy tờ do nước ngoài cấp, đã qua Cục Lãnh sự. Chiều đi Úc dùng NAATI, chiều vào Thái dùng tuyến lãnh sự.",
      },
      {
        q: "Giấy tờ nước ngoài dùng tại Thái Lan cần theo thứ tự nào?",
        a: "Chứng nhận trước, dịch sau. Giấy tờ được chứng nhận tại nước cấp (công chứng viên, rồi cơ quan có thẩm quyền hoặc bộ ngoại giao nước đó), sau đó hợp pháp hóa tại Đại sứ quán hoặc Tổng lãnh sự quán Thái Lan ở nước đó nếu chưa dùng được Apostille. Chỉ khi đó mới dịch sang tiếng Thái và đưa bản dịch tới Cục Lãnh sự. Dịch trước khi chứng nhận là nguyên nhân phổ biến nhất khiến phải làm lại toàn bộ hồ sơ.",
      },
      {
        q: "Giấy tờ hộ tịch có thời hạn bao lâu?",
        a: "Phần lớn cơ quan tiếp nhận muốn bản trích lục cấp trong vòng ba đến sáu tháng, và một số đại sứ quán áp mốc ba tháng chặt hơn cho giấy xác nhận tình trạng hôn nhân và lý lịch tư pháp. Vì cả chuỗi có thể mất hai đến ba tuần, hãy xin bản trích lục mới tại cơ quan quận trước rồi tính lịch từ ngày đó. Bản trích lục hết hạn trong lúc chờ tại đại sứ quán thì không cứu được, phải làm lại từ đầu.",
      },
      {
        q: "Vì sao bản dịch đúng vẫn bị trả lại?",
        a: "Các nguyên nhân lặp lại gồm: tên viết khác hộ chiếu, dịch từ bản photocopy chưa chứng thực thay vì bản trích lục mới, bỏ sót mặt sau, con dấu hoặc ghi chú viết tay, số hiệu văn bản không khớp bản gốc, tháo ghim khiến dấu giáp lai bị đứt, và bản gốc đã hết hạn. Tất cả đều là lỗi hình thức mà cán bộ xét duyệt có thể nêu ra mà không cần đánh giá chất lượng bản dịch.",
      },
      {
        q: "Có dịch trực tiếp giữa hai ngoại ngữ được không?",
        a: "Với đa số cặp ngôn ngữ, chúng tôi đi qua tiếng Anh hoặc tiếng Thái, và cơ quan tiếp nhận thường ưa cách này vì có thể đối chiếu bản trung gian. Nếu nơi tiếp nhận yêu cầu cặp trực tiếp, ví dụ giấy tờ tiếng Nhật nộp cho một bộ của Hàn Quốc, chúng tôi xác nhận trước xem họ có chấp nhận bản trung gian không, vì làm lại cả chuỗi tốn kém hơn nhiều so với việc hỏi trước. Hãy cho biết tên chính xác của cơ quan tiếp nhận.",
      },
    ],
  },

  id: {
    hreflang: "id",
    title: "Tanya Jawab Terjemahan Tersumpah dan Legalisasi di Thailand",
    description:
      "Penjelasan lengkap terjemahan dan legalisasi di Thailand: siapa yang berwenang, kapan perlu Departemen Konsuler Kemlu, tahap kedutaan, waktu berlakunya Apostille, cakupan NAATI, dan penyebab berkas ditolak.",
    h1: "Terjemahan dan Legalisasi Dokumen di Thailand — Tanya Jawab",
    lead:
      "Thailand tidak memiliki daftar penerjemah tersumpah yang dikelola negara. Dokumen yang dibawa ke luar negeri disahkan melalui Departemen Konsuler Kementerian Luar Negeri (Chaeng Watthana), yang mengesahkan rantai tanda tangan dan stempel, bukan menjamin pilihan kata terjemahan. Isi berikut mengikuti praktik Agustus 2026; biaya dan hari kerja bersifat perkiraan dan instansi penerima menentukan syarat akhir.",
    answerLabel: "Jawaban",
    pillarLabel: "Ikhtisar layanan penerjemahan",
    thaiSetLabel: "Pustaka tanya jawab bahasa Thai",
    notaryLabel: "Notary Public di Thailand",
    ctaTitle: "Belum yakin tingkat pengesahan mana yang dibutuhkan?",
    ctaText: "Kirim pindaian dokumen dan nama instansi penerima. Kami pastikan rantai prosesnya, perkiraan biaya, dan jumlah hari kerja sebelum pekerjaan dimulai.",
    disclaimer:
      "Informasi umum, bukan nasihat hukum, dan tidak menjamin hasil apa pun. Ketentuan dapat berubah — pastikan kepada instansi yang akan menerima dokumen Anda.",
    sourcesLabel:
      "Sumber utama: Departemen Konsuler Kementerian Luar Negeri Thailand; tabel status Konvensi Apostille 1961 HCCH; NAATI (Australia); peraturan layanan notarial Lawyers Council of Thailand.",
    items: [
      {
        q: "Apakah Thailand punya penerjemah tersumpah seperti di Indonesia?",
        a: "Tidak. Berbeda dengan Indonesia, Jerman, Prancis, atau Spanyol, Thailand tidak memiliki daftar penerjemah tersumpah yang diangkat pengadilan. Terjemahan di Thailand memperoleh bobot resmi melalui dua jalur: surat pernyataan keakuratan dari perusahaan atau penerjemah, dan untuk dokumen yang dipakai di luar negeri, pengesahan terjemahan oleh Departemen Konsuler Kemlu. Ketika instansi asing meminta sworn translation, yang dimaksud biasanya jalur konsuler ini atau penerjemah tersumpah di negara tersebut.",
      },
      {
        q: "Kapan terjemahan wajib dilegalisasi Departemen Konsuler?",
        a: "Ketika instansi pemerintah asing akan mengambil keputusan berdasarkan dokumen itu: berkas visa dan keimigrasian, pencatatan perkawinan di luar negeri, pendaftaran universitas, izin profesi, pendirian perusahaan, pengajuan ke pengadilan, dan transaksi properti. Untuk keperluan privat seperti arsip HRD, pendaftaran sekolah yang hanya meminta salinan tersertifikasi, atau audit internal, surat keakuratan perusahaan umumnya cukup. Tanyakan tertulis sebelum membayar seluruh rantai.",
      },
      {
        q: "Berapa biaya dan waktu di Departemen Konsuler?",
        a: "Tarif resmi Kemlu adalah 200 baht per dokumen untuk layanan biasa, biasanya diambil pada hari kerja kedua, dan 400 baht per dokumen untuk layanan kilat yang selesai di hari yang sama. Ini biaya negara, terpisah dari ongkos terjemahan dan kurir, dan dapat diubah oleh Kemlu. Waktu nyata dipengaruhi jumlah dokumen, kondisi berkas, dan antrean di Chaeng Watthana, jadi sediakan cadangan satu hari kerja.",
      },
      {
        q: "Apakah Thailand sudah bisa menerbitkan Apostille?",
        a: "Belum. Thailand menyerahkan instrumen aksesi Konvensi Apostille Den Haag 1961 pada Juni 2026 dan Konvensi berlaku bagi Thailand pada 28 Februari 2027. Sampai tanggal itu, dokumen Thailand untuk negara anggota tetap mengikuti rantai lama: penerjemahan, pengesahan Departemen Konsuler, lalu legalisasi di kedutaan atau konsulat negara tujuan di Bangkok. Pastikan status terkini ke Departemen Konsuler sebelum mengajukan.",
      },
      {
        q: "Setelah Apostille berlaku, apakah tahap kedutaan hilang?",
        a: "Untuk negara anggota Konvensi, Apostille dirancang menggantikan legalisasi kedutaan atas dokumen publik. Negara bukan anggota, termasuk beberapa negara Timur Tengah dan sejumlah yurisdiksi Asia, tetap meminta legalisasi kedutaan. Sebagian instansi penerima juga mempertahankan aturan internal sendiri, misalnya mensyaratkan terjemahan yang dibuat di negaranya. Apostille menggantikan satu mata rantai, bukan menggantikan penerjemahan atau diskresi instansi penerima.",
      },
      {
        q: "Apakah terjemahan NAATI berguna untuk dipakai di Thailand?",
        a: "Umumnya tidak. NAATI adalah lembaga sertifikasi Australia dan stempelnya yang diharapkan oleh Department of Home Affairs, universitas Australia, serta instansi negara bagian. Kantor distrik, Imigrasi, dan pengadilan Thailand tidak mengakui NAATI sebagai sertifikasi domestik; mereka meminta terjemahan bahasa Thai dengan pernyataan keakuratan dan, bila dokumen terbitan asing, sudah melalui Departemen Konsuler. Arah ke Australia pakai NAATI, arah ke Thailand pakai jalur konsuler.",
      },
      {
        q: "Bagaimana urutan yang benar untuk dokumen asing yang dipakai di Thailand?",
        a: "Sahkan dulu, terjemahkan kemudian. Dokumen disahkan di negara penerbit (notaris, lalu otoritas berwenang atau kementerian luar negeri negara itu), kemudian dilegalisasi oleh Kedutaan atau Konsulat Thailand di sana bila jalur Apostille belum tersedia. Barulah dokumen diterjemahkan ke bahasa Thai dan terjemahannya disahkan Departemen Konsuler. Menerjemahkan sebelum pengesahan adalah penyebab paling sering berkas harus diulang dari awal.",
      },
      {
        q: "Berapa lama masa berlaku dokumen catatan sipil?",
        a: "Sebagian besar instansi tujuan meminta kutipan tersertifikasi yang terbit dalam tiga sampai enam bulan terakhir, dan sejumlah kedutaan menerapkan batas tiga bulan yang lebih ketat untuk surat keterangan status perkawinan dan catatan kepolisian. Karena seluruh rantai dapat memakan dua sampai tiga minggu, ambil kutipan baru di kantor distrik lebih dulu dan hitung jadwal dari tanggal tersebut. Kutipan yang kedaluwarsa saat menunggu di kedutaan tidak bisa diselamatkan.",
      },
      {
        q: "Mengapa terjemahan yang sudah benar tetap ditolak?",
        a: "Penyebab yang berulang: ejaan nama berbeda dari paspor, menerjemahkan dari fotokopi yang belum disahkan alih-alih kutipan resmi terbaru, halaman belakang, stempel, atau catatan tulisan tangan tidak diterjemahkan, nomor dokumen tidak cocok dengan asli, staples dilepas sehingga stempel penghubung halaman terputus, dan dokumen sumber sudah kedaluwarsa. Semuanya cacat formal yang bisa dipakai petugas tanpa menilai mutu terjemahan.",
      },
      {
        q: "Bisakah menerjemahkan langsung antara dua bahasa asing?",
        a: "Untuk sebagian besar pasangan bahasa kami melewati bahasa Inggris atau Thai, dan instansi penerima biasanya justru menyukainya karena dapat memverifikasi teks perantara. Bila tujuan mensyaratkan pasangan langsung, misalnya dokumen Jepang untuk kementerian di Korea, kami pastikan lebih dulu apakah versi perantara diterima, sebab mengulang rantai jauh lebih mahal daripada bertanya di awal. Sebutkan nama resmi instansi penerima kepada kami.",
      },
    ],
  },
};
